import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import Field from "../components/Field";
import FormMessage from "../components/FormMessage";
import { ApiError, apiFetch } from "../lib/api";

type InviteInfo = {
  email: string;
  fullName: string;
  preferredName: string | null;
  hasPassword: boolean;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; info: InviteInfo }
  | { kind: "error"; message: string };

export default function Invite() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: "This page needs an invite token." });
      return;
    }
    apiFetch<InviteInfo>(`/api/invites/${encodeURIComponent(token)}`)
      .then((info) => setState({ kind: "ok", info }))
      .catch((err: unknown) => {
        const message =
          err instanceof ApiError ? err.message : "Couldn't load this invite.";
        setState({ kind: "error", message });
      });
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (password.length < 10) {
      setSubmitError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setSubmitError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        body: { password },
      });
      navigate("/almost-there", { replace: true });
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : "Something went wrong.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === "loading") {
    return (
      <AuthShell eyebrow="Invite" title="one moment…">
        <p style={{ textAlign: "center", opacity: 0.7 }}>Checking your link.</p>
      </AuthShell>
    );
  }

  if (state.kind === "error") {
    return (
      <AuthShell eyebrow="Invite" title="link expired.">
        <FormMessage tone="error">{state.message}</FormMessage>
        <p style={{ textAlign: "center", marginTop: "1rem" }}>
          <Link to="/" className="btn btn--ghost">
            Back to the landing page
          </Link>
        </p>
      </AuthShell>
    );
  }

  const greetingName =
    state.info.preferredName ?? state.info.fullName.split(" ")[0] ?? "you";

  return (
    <AuthShell
      eyebrow="Welcome in"
      title={`hi, ${greetingName.toLowerCase()}.`}
      intro={
        <>
          Pick a password for <strong>{state.info.email}</strong>. At least 10
          characters — a passphrase works great.
        </>
      }
    >
      {submitError && <FormMessage tone="error">{submitError}</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          label="Password"
          type="password"
          required
          minLength={10}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <Field
          label="Confirm password"
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
        />
        <button
          type="submit"
          className="btn btn--primary"
          disabled={submitting}
          style={{ width: "100%", marginTop: "0.5rem" }}
        >
          {submitting ? "Saving…" : "Set password & continue"}
        </button>
      </form>
    </AuthShell>
  );
}
